import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { formatDateOnly } from "@mediatechgroup/calendar-date";
import { useSelector, useDispatch } from "react-redux";
import { selectMembershipDetails, setMembership } from "../../store/slices/membershipSlice";
import { getFutureInvoicesByMember, updateBillingHistoryInvoice, deleteInvoice } from "../../api/invoiceAPI";
import { getMemberById } from "../../api/membershipAPI";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getPageBackground,
} from "../../utils/themeUtils";
import { selectIsAdmin, selectIsStaff } from "../../store/slices/authSlice";
import MemberInvoiceTooltip from "../invoice/membership/MemberInvoiceTooltip";
import { toast } from "react-toastify";
import EditBillingInvoiceModal from "./EditBillingInvoiceModal";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import { captureError } from "../../utils/sentry";
import { getPaymentErrorMessage } from "../../utils/paymentErrors";
import ChargeStatusBadge from "./BillingHistory/ChargeStatusBadge";
import BillingPager from "./BillingHistory/BillingPager";
import { defaultFuturePageSize, pageSizeOptions } from "./BillingHistory/billingPaging";

interface MyComponentProps {
  refreshApiCalls: number;
  setRefreshApiCalls: Dispatch<SetStateAction<number>>;
  /** Where the billing panel is embedded; the staff membership page opens short. */
  source?: string;
}

const MemberFutureInvoices: React.FC<MyComponentProps> = ({
  refreshApiCalls,
  setRefreshApiCalls,
  source,
}) => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch();
  const membership: any = useSelector(selectMembershipDetails);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // lodaing state
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [, setIsloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tooltipData, setTooltipData] = useState<null | {
    x: number;
    y: number;
    invoice: any;
  }>(null);
  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  // 2 rows on the staff membership page, 5 everywhere else (see billingPaging).
  const [itemsPerPage, setItemsPerPage] = useState<number>(() => defaultFuturePageSize(source));
  const token = localStorage.getItem("authToken");
  const isAdmin = useSelector(selectIsAdmin);
  const isStaff = useSelector(selectIsStaff);

  const fetchInvoices = async () => {
    setFetchLoading(true);
    try {
      const res = await getFutureInvoicesByMember(token, membership?.id);
      if (res.success) {
        setInvoices(res.data);
        setFetchLoading(false);
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
    } finally {
      setLoading(false);
    }
  };

  const fetchMembershipDetails = async () => {
    try {
      const response = await getMemberById(token, membership.id);

      // Update membership details in Redux store
      if (response && response.data) {
        dispatch(setMembership(response?.data));
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
    }
  };

  useEffect(() => {
    if (membership?.id) {
      fetchInvoices();
    }
  }, [membership?.id, refreshApiCalls]);

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Current item according to pagination and search
  const currentItems = invoices?.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEdit = (invoiceId: string) => {
    if (isAdmin || isStaff) {
      const invoice = invoices?.find((inv) => inv.id === invoiceId);
      setSelectedInvoice(invoice);
      setIsEditModalOpen(true);
    }
  };

  // edit invoice
  const handleSubmit = async (updatedInvoice:any) => {
    setIsloading(true);
    try {
      // Update invoice
      const payload = {
        invoiceId: updatedInvoice?.id,
        amount: updatedInvoice?.amount,
        description: updatedInvoice?.description,
        dueDate: updatedInvoice?.due_date,
        status: updatedInvoice?.status,
        tenantId: updatedInvoice?.tenant_id,
        metadata: updatedInvoice?.metadata,
        // The server refuses an amount change unless the editor says it was
        // deliberate (a note or date edit used to erase charges to $0).
        amountChanged: updatedInvoice?.amountChanged === true,
      };
      const res = await updateBillingHistoryInvoice(payload, token);

      if (res?.status === 200) {
        toast.success(res?.data?.message);
        // re-fetch
        fetchInvoices();
        fetchMembershipDetails();
        setRefreshApiCalls(prev => prev + 1);
      } else {
        toast.error("Invoice is not updated");
      }
    } catch (err) {
      // Was empty under "Axios interceptor handles Sentry capture" — it does
      // not, and the finally below closes the modal regardless, so a failed
      // edit looked exactly like a successful one: modal shut, no message.
      captureError(err, { component: "MemberFutureInvoices", operation: "updateInvoice" }, "warning");
      toast.error(getPaymentErrorMessage(err, "Invoice is not updated"));
    } finally {
      setIsEditModalOpen(false);
      setIsloading(false);
    }
  };

  // delete invoice handlers
  const handleDelete = (invoiceId: string) => {
    setPendingDeleteId(invoiceId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;

    setIsloading(true);
    try {
      const res = await deleteInvoice(token, pendingDeleteId);

      if (res?.success) {
        toast.success(res?.message || "Invoice deleted successfully");
        // re-fetch
        fetchInvoices();
        fetchMembershipDetails();
        setRefreshApiCalls(prev => prev + 1);
      } else {
        toast.error(res?.message || "Failed to delete invoice");
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
      toast.error("An error occurred while deleting the invoice");
    } finally {
      setShowDeleteModal(false);
      setPendingDeleteId(null);
      setIsloading(false);
    }
  };

  const handleCancelDelete = () => {
    setPendingDeleteId(null);
    setShowDeleteModal(false);
  };

  if (loading)
    return (
      // Was a full screen tall, which pushed everything below the billing
      // tables off the page until the future invoices arrived.
      <div className="w-full flex justify-center items-center py-4">
        <div className="flex justify-center items-center">
          <div className="w-6 h-6 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-500 dark:text-gray-400">Loading future invoices...</span>
        </div>
      </div>
    );

  // Always render, but hide if no invoices
  const hasInvoices = invoices?.length > 0;

  return (
    <div
      data-testid="future-invoices"
      className={`mt-6 rounded-lg ${getCardBackground(isDarkMode)} ${!hasInvoices ? 'hidden' : ''}`}
    >
      {/* No side padding: the table lines up with the Billing History table
          above it, which sits flush in the same container. */}
      <h2
        className={`text-2xl font-bold flex-shrink-0 mb-3 ${getPrimaryText(
          isDarkMode
        )}`}
      >
        Future Invoices
      </h2>
      <div className="w-full flex justify-end">
        {/* Small loader for search/filter actions */}
        {fetchLoading && (
          <div
            className={`flex items-center space-x-4 mb-4 ${getMutedText(
              isDarkMode
            )}`}
          >
            <div
              className={`w-4 h-4 border-2 ${
                isDarkMode ? "border-gray-800" : "border-gray-400"
              } border-t-transparent rounded-full animate-spin`}
            ></div>
            <span className="text-sm">Updating results...</span>
          </div>
        )}
      </div>
      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table
          className={`w-full text-sm text-left rtl:text-right ${getMutedText(
            isDarkMode
          )}`}
        >
          <thead
            className={`text-xs ${getSecondaryText(
              isDarkMode
            )} uppercase ${getPageBackground(isDarkMode)}`}
          >
            <tr>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Description
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Amount
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Due Date
              </th>
            </tr>
          </thead>

          <tbody>
            {currentItems?.map((inv, idx) => (
              <tr
                key={inv.id}
                className={`border-b cursor-pointer ${
                  isDarkMode
                    ? "bg-sky-900 border-gray-700"
                    : "bg-sky-100 border-gray-200"
                }`}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltipData({
                    x: rect.left + rect.width / 2,
                    y: rect.bottom + window.scrollY,
                    invoice: inv,
                  });
                }}
              >
                {/* Description */}
                <td
                  className={`px-6 py-4 ${getPrimaryText(
                    isDarkMode
                  )}`}
                >
                  <div className="flex items-center gap-2">
                    {/* Invoice Badge */}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                      Invoice
                    </span>
                    {/* A draft is not sent yet, and once its date comes it
                        counts toward the balance: say which ones are drafts. */}
                    {inv?.post_status === "draft" && (
                      <ChargeStatusBadge invoice={inv} isDarkMode={isDarkMode} />
                    )}
                    {/* Description or Invoice Type */}
                    {(inv?.description || inv?.metadata?.display_invoice_type) && (
                      <span className="truncate" title={inv.description || inv.metadata.display_invoice_type}>
                        {inv.description || inv.metadata.display_invoice_type}
                      </span>
                    )}
                  </div>
                </td>

                {/* Amount */}
                <td
                  className={`px-6 py-4 ${getPrimaryText(
                    isDarkMode
                  )}`}
                >
                  ${inv?.amount?.toFixed(2)}
                </td>

                {/* Due Date */}
                <td
                  className={`px-6 py-4 ${getPrimaryText(
                    isDarkMode
                  )}`}
                >
                  {formatDateOnly(inv?.due_date)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination — one line, same control as Billing History */}
      <BillingPager
        idPrefix="future-invoices"
        className="mt-3"
        total={invoices?.length || 0}
        page={currentPage}
        pageSize={itemsPerPage}
        onPageChange={handlePageChange}
        onPageSizeChange={(size) => {
          setItemsPerPage(size);
          setCurrentPage(1);
        }}
        pageSizeOptions={pageSizeOptions(defaultFuturePageSize(source), itemsPerPage)}
        itemLabel="future invoices"
        itemLabelSingular="future invoice"
        previousLabel="Earlier future invoices"
        nextLabel="Later future invoices"
      />
      {/* Modal */}
      {/* <EditInvoiceModal
        isOpen={isEditModalOpen}
        isLoading={isLoading}
        invoice={selectedInvoice}
        onClose={() => setIsEditModalOpen(false)}
        onChange={setSelectedInvoice}
        onSubmit={handleSubmit}
      /> */}
      <EditBillingInvoiceModal
        invoice={selectedInvoice}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onChange={setSelectedInvoice}
        onSubmit={handleSubmit}
        isLoading={false}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        title="Delete Future Invoice"
        message="Are you sure you want to delete this future invoice? This action cannot be undone."
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Invoice Tooltip/Popover */}
      {tooltipData && (
        <>
          {/* Backdrop to catch clicks outside */}
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-20"
            onClick={() => setTooltipData(null)}
          />
          {/* Tooltip */}
          <MemberInvoiceTooltip
            invoice={tooltipData.invoice}
            onClose={() => setTooltipData(null)}
            isAdminOrStaff={isAdmin || isStaff}
            {...((isAdmin || isStaff) && {
              onEdit: (invoiceId) => {
                setTooltipData(null);
                handleEdit(invoiceId);
              },
              onDelete: (invoice) => {
                setTooltipData(null);
                handleDelete(invoice.id);
              },
            })}
          />
        </>
      )}
    </div>
  );
};

export default MemberFutureInvoices;
